import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Container
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import usePermissions from '../hooks/usePermissions';
import useNotification from '../hooks/useNotification';
import PermissionTreeView from '../components/permissions/PermissionTreeView';
import CreateRootPermissionModal from '../components/permissions/CreateRootPermissionModal';
import AddChildPermissionModal from '../components/permissions/AddChildPermissionModal';
import EditPermissionModal from '../components/permissions/EditPermissionModal';
import ViewPermissionModal from '../components/permissions/ViewPermissionModal';
import DeletePermissionDialog from '../components/permissions/DeletePermissionDialog';

/**
 * PermissionManagement component
 * Main page for managing system and project permissions with hierarchical tree view
 */
const PermissionManagement = () => {
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0); // 0 = System, 1 = Project
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTree, setFilteredTree] = useState([]);

  // Modals state
  const [createRootModalOpen, setCreateRootModalOpen] = useState(false);
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);
  const [permissionToEdit, setPermissionToEdit] = useState(null);
  const [permissionToView, setPermissionToView] = useState(null);
  const [permissionToDelete, setPermissionToDelete] = useState(null);

  // Use permissions hook
  const {
    loading,
    error,
    permissionTree,
    getPermissionTree,
    createPermission,
    updatePermission,
    deletePermission,
    movePermission,
    getPermissionUsage,
    clearError,
    clearPermissionTreeCache
  } = usePermissions();

  // Determine current category based on tab
  const currentCategory = tabValue === 0 ? 'system' : 'project';

  // Fetch permission tree on component mount and tab change
  useEffect(() => {
    getPermissionTree(currentCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCategory]);

  // Filter tree based on search query
  useEffect(() => {
    if (!searchQuery) {
      setFilteredTree(permissionTree || []);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filterTree = (nodes) => {
      if (!nodes) return [];
      return nodes.reduce((acc, node) => {
        // Check if current node matches
        const nameMatches = node.name.toLowerCase().includes(query);
        const pathMatches = node.path.toLowerCase().includes(query);
        const descMatches = node.description && node.description.toLowerCase().includes(query);

        // Recursively filter children
        const filteredChildren = node.children ? filterTree(node.children) : [];

        // Include node if it matches OR has matching children
        if (nameMatches || pathMatches || descMatches || filteredChildren.length > 0) {
          acc.push({
            ...node,
            children: filteredChildren
          });
        }

        return acc;
      }, []);
    };

    setFilteredTree(filterTree(permissionTree));
  }, [permissionTree, searchQuery]);

  // Fetch permission tree with force refresh
  const fetchTree = async () => {
    try {
      // Clear cache and force refresh
      clearPermissionTreeCache(currentCategory);
      const result = await getPermissionTree(currentCategory, true);
      console.log('Permission Tree Fetched:', {
        category: currentCategory,
        rootCount: result?.length || 0,
        data: result
      });
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      showNotification({
        message: err.message || 'Failed to fetch permissions',
        type: 'error'
      });
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchQuery(''); // Clear search on tab change
  };

  // Handle create root permission
  const handleCreateRoot = () => {
    setCreateRootModalOpen(true);
  };

  // Handle add child permission
  const handleAddChild = (parentPermission) => {
    setSelectedParent(parentPermission);
    setAddChildModalOpen(true);
  };

  // Handle edit permission
  const handleEdit = (permission) => {
    setPermissionToEdit(permission);
    setEditModalOpen(true);
  };

  // Handle view permission
  const handleView = async (permission) => {
    setPermissionToView(permission);
    setViewModalOpen(true);
  };

  // Handle delete permission
  const handleDelete = (permission) => {
    setPermissionToDelete(permission);
    setDeleteDialogOpen(true);
  };

  // Handle move up
  const handleMoveUp = async (permission) => {
    try {
      await movePermission(permission.id, 'up');
      clearPermissionTreeCache(currentCategory); // Clear cache
      await fetchTree(); // Refresh tree
      showNotification({
        message: 'Permission moved up successfully',
        type: 'success'
      });
    } catch (err) {
      showNotification({
        message: err.message || 'Failed to move permission',
        type: 'error'
      });
    }
  };

  // Handle move down
  const handleMoveDown = async (permission) => {
    try {
      await movePermission(permission.id, 'down');
      clearPermissionTreeCache(currentCategory); // Clear cache
      await fetchTree(); // Refresh tree
      showNotification({
        message: 'Permission moved down successfully',
        type: 'success'
      });
    } catch (err) {
      showNotification({
        message: err.message || 'Failed to move permission',
        type: 'error'
      });
    }
  };

  // Handle permission created
  const handlePermissionCreated = async () => {
    clearPermissionTreeCache(currentCategory); // Clear cache
    await fetchTree(); // Refresh tree
    setCreateRootModalOpen(false);
    setAddChildModalOpen(false);
    setSelectedParent(null);
    showNotification({
      message: 'Permission created successfully',
      type: 'success'
    });
  };

  // Handle permission updated
  const handlePermissionUpdated = async () => {
    clearPermissionTreeCache(currentCategory); // Clear cache
    await fetchTree(); // Refresh tree
    setEditModalOpen(false);
    setPermissionToEdit(null);
    showNotification({
      message: 'Permission updated successfully',
      type: 'success'
    });
  };

  // Handle permission deleted
  const handlePermissionDeleted = async () => {
    clearPermissionTreeCache(currentCategory); // Clear cache
    await fetchTree(); // Refresh tree
    setDeleteDialogOpen(false);
    setPermissionToDelete(null);
    showNotification({
      message: 'Permission deleted successfully',
      type: 'success'
    });
  };

  // Handle modal close
  const handleCloseModals = () => {
    setCreateRootModalOpen(false);
    setAddChildModalOpen(false);
    setEditModalOpen(false);
    setViewModalOpen(false);
    setDeleteDialogOpen(false);
    setSelectedParent(null);
    setPermissionToEdit(null);
    setPermissionToView(null);
    setPermissionToDelete(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Permission Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage system and project level permissions with hierarchical structure
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Tabs and Actions */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="System Permissions" />
            <Tab label="Project Permissions" />
          </Tabs>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateRoot}
            sx={{ mb: 1 }}
          >
            Add Root Permission
          </Button>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search permissions by name, path, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* Permission Tree */}
      <Paper sx={{ p: 3, maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredTree.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchQuery ? 'No permissions found' : 'No permissions available'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Click "Add Root Permission" to create your first permission'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Debug Info */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Showing {filteredTree.length} root-level permissions
            </Typography>
            <PermissionTreeView
              permissions={filteredTree}
              onAddChild={handleAddChild}
              onEdit={handleEdit}
              onView={handleView}
              onDelete={handleDelete}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
            />
          </>
        )}
      </Paper>

      {/* Modals */}
      <CreateRootPermissionModal
        open={createRootModalOpen}
        category={currentCategory}
        onClose={handleCloseModals}
        onSuccess={handlePermissionCreated}
      />

      <AddChildPermissionModal
        open={addChildModalOpen}
        parentPermission={selectedParent}
        category={currentCategory}
        onClose={handleCloseModals}
        onSuccess={handlePermissionCreated}
      />

      <EditPermissionModal
        open={editModalOpen}
        permission={permissionToEdit}
        onClose={handleCloseModals}
        onSuccess={handlePermissionUpdated}
      />

      <ViewPermissionModal
        open={viewModalOpen}
        permission={permissionToView}
        onClose={handleCloseModals}
        getPermissionUsage={getPermissionUsage}
      />

      <DeletePermissionDialog
        open={deleteDialogOpen}
        permission={permissionToDelete}
        onClose={handleCloseModals}
        onSuccess={handlePermissionDeleted}
        deletePermission={deletePermission}
        getPermissionUsage={getPermissionUsage}
      />
    </Container>
  );
};

export default PermissionManagement;
